/**
 * @file     types.ts
 * @purpose  Shared types for the Relta chat engine: the server-owned
 *           conversation state (v2, text-only — no MLS card blobs), turn
 *           payloads, and the per-turn trace.
 */

import type { AiFilters } from '@/services/mls/aiFilterSchema';
import type { AgentProposal, ChartWidget } from './capabilities/types';

export type ChatAction = 'ask' | 'present' | 'answer' | 'focus' | 'commute_handoff';

export interface ChatTurnMessage {
  role: 'user' | 'assistant';
  content: string;
  /** True when this assistant turn changed the search and showed match cards
   *  (the client re-hydrates cards for the last such turn on resume). */
  hadMatches?: boolean;
  /** Listing keys shown with this turn — identifiers only, never MLS rows
   *  (ADR-009). The client re-fetches every card from the audited API on
   *  resume, so ALL history cards render, not just the last turn's. */
  presentedKeys?: string[];
  error?: boolean;
}

/** Server-owned conversation state. v2 stores NO listing data: messages are
 *  text-only, cards are client-side decoration re-fetched from the audited
 *  listings API. */
export interface ConversationStateV2 {
  v: 2;
  messages: ChatTurnMessage[];
  filters: AiFilters;
  count: number | null;
  offset: number;
  /** Non-search buyer statements the summary carries forward (capped). */
  notes: string[];
  /** Listing keys already presented in this conversation (identifiers only;
   *  the text-only storage contract covers listing DATA, not references). */
  seenListingKeys: string[];
  updatedAt: number;
}

/** A directive the client applies to the live map (AI Search on /properties).
 *  Aggregate/UI intent only, never MLS rows: the model names a layer or the
 *  client resolves a focus from a card it already holds (ADR-009 safe). */
export type MapCommand =
  | { type: 'setLayer'; key: 'transit' | 'schools' | 'parks' | 'density' | 'postal'; on: boolean }
  | { type: 'focusListing'; listingKey: string; lat: number; lng: number };

/** A one-tap offer to undo a manual filter edit that cut the buyer's matches.
 *  Surfaced, never applied by the server; the client applies on the buyer's tap. */
export interface RevertSuggestion {
  /** The search set to restore to (previous values merged into the current set). */
  filters: AiFilters;
  /** Result count for that restored set (recomputed server-side). */
  count: number | null;
  /** Buyer-facing chip label, e.g. "Restore max price to $800k (422 matches)". */
  summary: string;
}

export interface MortgageNumbers {
  price: number;
  downPayment: number;
  downPct: number;
  rate: number;
  years: number;
  monthlyPrincipalAndInterest: number;
  monthlyTotal: number;
}

export interface TurnPayload {
  reply: string;
  action: ChatAction;
  matchIndex: 0 | 1 | null;
  /** Post-turn filter state (applied via the update_search tool). */
  filters: AiFilters;
  count: number | null;
  offset: number;
  /** The update_search tool ran this turn. */
  searchChanged: boolean;
  /** The site presents a home with this reply (band or explicit ask). */
  presented: boolean;
  mortgage: MortgageNumbers | null;
  /** Ledger-backed proposals awaiting the buyer's Confirm tap. */
  proposals: AgentProposal[];
  /** Deterministic trigger: render the licensed-agent CTA under this reply. */
  agentCta: boolean;
  /** Directives for the live map (AI Search view); empty on the home page. */
  mapCommands: MapCommand[];
  /** Data charts to render under the reply. */
  widgets: ChartWidget[];
  /** One-tap offer to undo a harmful manual filter edit; null when none. */
  revertSuggestion: RevertSuggestion | null;
}

export interface TurnTrace {
  promptVersion: string;
  model: string;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  toolCalls: number;
  retries: number;
  action: ChatAction;
  resultCount: number | null;
}

export function emptyState(): ConversationStateV2 {
  return { v: 2, messages: [], filters: {}, count: null, offset: 0, notes: [], seenListingKeys: [], updatedAt: Date.now() };
}

/** Stored transcripts are bounded: the engine reads only the tail, the UI can
 *  page later if ever needed. Keeps jsonb rows from growing without limit. */
export const MAX_STORED_MESSAGES = 60;
const MAX_STORED_CHARS = 4000;
const MAX_STORED_NOTES = 6;
const MAX_SEEN_KEYS = 50;

/** Normalize ANY stored/inbound blob (v1 client-authored, v2, or hostile) to a
 *  valid bounded v2 state: text-only messages, card blobs stripped, arrays and
 *  strings capped. Every read path and every client-supplied state goes
 *  through here, so a malformed row can never brick a conversation turn. */
export function migrateState(raw: unknown): ConversationStateV2 {
  const r = (raw ?? {}) as Record<string, unknown>;
  const messages = Array.isArray(r.messages)
    ? (r.messages as Array<Record<string, unknown>>)
        .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string')
        .slice(-MAX_STORED_MESSAGES)
        .map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: (m.content as string).slice(0, MAX_STORED_CHARS),
          ...(m.hadMatches === true || (Array.isArray(m.matches) && m.matches.length > 0) ? { hadMatches: true } : {}),
          ...(Array.isArray(m.presentedKeys) && (m.presentedKeys as unknown[]).length
            ? { presentedKeys: (m.presentedKeys as unknown[]).filter((k): k is string => typeof k === 'string' && k.length < 80).slice(0, 5) }
            : {}),
          ...(m.error === true ? { error: true } : {}),
        }))
    : [];
  const notes = Array.isArray(r.notes)
    ? (r.notes as unknown[]).filter((n): n is string => typeof n === 'string').map((n) => n.slice(0, 160)).slice(-MAX_STORED_NOTES)
    : [];
  const seenListingKeys = Array.isArray(r.seenListingKeys)
    ? (r.seenListingKeys as unknown[]).filter((k): k is string => typeof k === 'string' && k.length < 80).slice(-MAX_SEEN_KEYS)
    : [];
  return {
    v: 2,
    messages,
    filters: (r.filters && typeof r.filters === 'object' && !Array.isArray(r.filters) ? r.filters : {}) as AiFilters,
    count: typeof r.count === 'number' ? r.count : null,
    offset: typeof r.offset === 'number' && r.offset >= 0 ? Math.min(r.offset, 200) : 0,
    notes,
    seenListingKeys,
    updatedAt: typeof r.updatedAt === 'number' ? r.updatedAt : Date.now(),
  };
}

/** Presentation band: the site shows an individual home only once the search
 *  is narrow enough to present like a realtor (or the buyer explicitly asks
 *  for the next one). Shared by prompt, route, and client. */
export const PRESENT_MAX = 15;
/** Homes presented per turn: one at a time, like a showing. */
export const PRESENT_PER_TURN = 1;


